"use client";

import { usePathname, useRouter } from "next/navigation";
import { useMemo } from "react";

import {
  DEFAULT_LOCALE,
  isLocalizedLocale,
  LOCALE_FLAGS,
  LOCALE_LABELS,
  LOCALIZED_LOCALES,
  stripLocaleFromPathname,
  SupportedLocale,
  withLocalePath,
  WORKSPACE_LOCALE_COOKIE,
} from "@/lib/i18n/locales";
import { isLocalizedSeoPath } from "@/lib/i18n/seo";

export default function LanguageSwitcher() {
  const pathname = usePathname();
  const router = useRouter();

  const basePath = useMemo(() => stripLocaleFromPathname(pathname ?? "/"), [pathname]);
  const currentLocale = useMemo<SupportedLocale>(() => {
    if (!pathname) {
      return DEFAULT_LOCALE;
    }
    const segment = pathname.split("/")[1];
    return segment && isLocalizedLocale(segment) ? segment : DEFAULT_LOCALE;
  }, [pathname]);

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextLocale = event.target.value as SupportedLocale;
    const hasLocalizedPath = isLocalizedSeoPath(basePath);
    const nextPath = hasLocalizedPath ? withLocalePath(nextLocale, basePath) : withLocalePath(nextLocale, "/");
    document.cookie = `${WORKSPACE_LOCALE_COOKIE}=${nextLocale}; path=/; max-age=31536000`;
    router.push(nextLocale === DEFAULT_LOCALE && !hasLocalizedPath ? basePath : nextPath);
  };

  return (
    <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
      <span className="sr-only">Language</span>
      <span className="relative inline-flex items-center sm:hidden">
        <span className="rounded-full border border-gray-300 bg-white px-2 py-1 text-xs">{LOCALE_FLAGS[currentLocale]}</span>
        <select
          aria-label="Select language"
          value={currentLocale}
          onChange={handleChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 text-transparent"
        >
          {[DEFAULT_LOCALE, ...LOCALIZED_LOCALES].map(locale => (
            <option key={locale} value={locale} className="text-gray-900">
              {LOCALE_FLAGS[locale]} {LOCALE_LABELS[locale]}
            </option>
          ))}
        </select>
      </span>
      <select
        aria-label="Select language"
        value={currentLocale}
        onChange={handleChange}
        className="hidden rounded-full border border-gray-300 bg-white px-2 py-1 text-xs sm:inline-flex sm:text-sm"
      >
        {[DEFAULT_LOCALE, ...LOCALIZED_LOCALES].map(locale => (
          <option key={locale} value={locale}>
            {LOCALE_FLAGS[locale]} {LOCALE_LABELS[locale]}
          </option>
        ))}
      </select>
    </label>
  );
}
